import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { LocalizedApiError } from '@/features/audit-issues/LocalizedApiError';
import type { DispatchDeleteBlocker } from './types';

export interface DispatchDeleteDialogRequestSummary {
  trip_code?: string | null;
  status?: string | null;
  linked_inbound_lot_id?: string | null;
  requested_date?: string | null;
  location?: string | null;
}

export interface DispatchDeleteDialogPreview {
  purge_after?: string | null;
  inbound_lot_count?: number;
  inbound_package_count?: number;
  dismantling_session_count?: number;
  dismantling_output_count?: number;
  inventory_movement_count?: number;
  can_delete?: boolean;
  blocker_count?: number;
  blockers?: DispatchDeleteBlocker[];
}

interface DeleteDispatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: DispatchDeleteDialogRequestSummary | null;
  preview?: DispatchDeleteDialogPreview | null;
  previewLoading?: boolean;
  previewError?: unknown;
  isPending: boolean;
  error?: unknown;
  onConfirm: (reason: string) => void;
}

const formatPurgeAfter = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const countOrZero = (value: number | undefined) => value ?? 0;

/**
 * Confirmation dialog for deleting a dispatch chain. This is intentionally
 * different from "cancel" / "reject": the backend will hide the dispatch,
 * linked inbound lot, dismantling work, and inventory movements from normal
 * operations while preserving them for the 30-day recovery window.
 */
export const DeleteDispatchDialog = ({
  open,
  onOpenChange,
  request,
  preview,
  previewLoading = false,
  previewError,
  isPending,
  error,
  onConfirm,
}: DeleteDispatchDialogProps) => {
  const { t } = useTranslation('operations');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setReasonError(null);
    }
  }, [open]);

  const blockers = useMemo(() => preview?.blockers ?? [], [preview?.blockers]);
  const blocked =
    preview?.can_delete === false || blockers.length > 0 || countOrZero(preview?.blocker_count) > 0;
  const dispatchOnlyDelete =
    !!preview && !request?.linked_inbound_lot_id && countOrZero(preview.inbound_lot_count) === 0;
  const canSubmit =
    !isPending &&
    !previewLoading &&
    !previewError &&
    !blocked &&
    !!preview &&
    reason.trim().length > 0;

  const submit = () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonError(
        t('efs.operations.dispatch.delete_dialog.reason_required', {
          defaultValue: '削除理由を入力してください。',
        }),
      );
      reasonRef.current?.focus();
      return;
    }
    setReasonError(null);
    onConfirm(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={isPending ? undefined : onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('efs.operations.dispatch.delete_dialog.title', {
              defaultValue: '配車表と関連データを削除',
            })}
          </DialogTitle>
          <DialogDescription>
            {t('efs.operations.dispatch.delete_dialog.subtitle', {
              defaultValue:
                'キャンセルや差し戻しではありません。関連する入庫・解体・在庫異動を通常画面から外し、30日間だけ復元できる状態で保管します。',
            })}
          </DialogDescription>
        </DialogHeader>

        {request ? (
          <div className="space-y-2 rounded-md border border-efs-border bg-efs-bg-subtle p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-efs-text-muted">
                {request.trip_code || '—'}
              </span>
              <span className="rounded-full bg-efs-bg px-2 py-0.5 text-xs text-efs-text-muted">
                {request.status || '—'}
              </span>
            </div>
            <div className="text-efs-text-primary">
              {request.requested_date ?? '—'} · {request.location ?? '—'}
            </div>
            <div className="text-xs text-efs-text-muted">
              {t('efs.operations.dispatch.delete_dialog.linked_inbound_lot', {
                lotId: request.linked_inbound_lot_id || '—',
                defaultValue: '連結入庫単: {{lotId}}',
              })}
            </div>
          </div>
        ) : null}

        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
          {previewLoading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {t('efs.operations.dispatch.delete_dialog.loading_preview', {
                defaultValue: '影響範囲を確認しています…',
              })}
            </span>
          ) : preview ? (
            <div className="space-y-3">
              <p className="font-medium">
                {t('efs.operations.dispatch.delete_dialog.immediate_effect', {
                  defaultValue:
                    '削除後、この配車表と関連データは派車表・入庫・解体・在庫の通常一覧からすぐ消えます。',
                })}
              </p>
              <p>
                {t('efs.operations.dispatch.delete_dialog.retention_notice', {
                  purgeAfter: formatPurgeAfter(preview.purge_after),
                  defaultValue: '完全削除予定: {{purgeAfter}}',
                })}
              </p>
              {dispatchOnlyDelete ? (
                <div className="rounded border border-orange-400 bg-orange-100 p-2 text-orange-950 dark:border-orange-700 dark:bg-orange-950 dark:text-orange-100">
                  <p className="inline-flex items-start gap-1 font-medium">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>
                      {t('efs.operations.dispatch.delete_dialog.dispatch_only_warning', {
                        defaultValue:
                          'この配車表は入荷ロットと連結されていないため、削除されるのは配車表のみです。手動で作成した入荷ロットがある場合は、入荷管理から別途削除してください。',
                      })}
                    </span>
                  </p>
                </div>
              ) : null}
              <dl className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <dt className="text-efs-text-muted">
                    {t('efs.operations.dispatch.delete_dialog.inbound_lots_label', {
                      defaultValue: '入庫',
                    })}
                  </dt>
                  <dd className="font-semibold">
                    {t('efs.operations.dispatch.delete_dialog.count_unit', {
                      count: countOrZero(preview.inbound_lot_count),
                      defaultValue: '{{count}} 件',
                    })}
                  </dd>
                </div>
                <div>
                  <dt className="text-efs-text-muted">
                    {t('efs.operations.dispatch.delete_dialog.packages_label', {
                      defaultValue: '包装',
                    })}
                  </dt>
                  <dd className="font-semibold">
                    {t('efs.operations.dispatch.delete_dialog.count_unit', {
                      count: countOrZero(preview.inbound_package_count),
                      defaultValue: '{{count}} 件',
                    })}
                  </dd>
                </div>
                <div>
                  <dt className="text-efs-text-muted">
                    {t('efs.operations.dispatch.delete_dialog.sessions_label', {
                      defaultValue: '解体',
                    })}
                  </dt>
                  <dd className="font-semibold">
                    {t('efs.operations.dispatch.delete_dialog.count_unit', {
                      count: countOrZero(preview.dismantling_session_count),
                      defaultValue: '{{count}} 件',
                    })}
                  </dd>
                </div>
                <div>
                  <dt className="text-efs-text-muted">
                    {t('efs.operations.dispatch.delete_dialog.outputs_label', {
                      defaultValue: '産出物',
                    })}
                  </dt>
                  <dd className="font-semibold">
                    {t('efs.operations.dispatch.delete_dialog.count_unit', {
                      count: countOrZero(preview.dismantling_output_count),
                      defaultValue: '{{count}} 件',
                    })}
                  </dd>
                </div>
                <div>
                  <dt className="text-efs-text-muted">
                    {t('efs.operations.dispatch.delete_dialog.movements_label', {
                      defaultValue: '在庫異動',
                    })}
                  </dt>
                  <dd className="font-semibold">
                    {t('efs.operations.dispatch.delete_dialog.count_unit', {
                      count: countOrZero(preview.inventory_movement_count),
                      defaultValue: '{{count}} 件',
                    })}
                  </dd>
                </div>
              </dl>
              {blocked ? (
                <div className="rounded border border-destructive/40 bg-destructive/10 p-2 text-destructive">
                  <p className="inline-flex items-center gap-1 font-medium">
                    <AlertTriangle className="h-4 w-4" aria-hidden />
                    {t('efs.operations.dispatch.delete_dialog.blocked', {
                      defaultValue: '後続データがあるため削除できません。',
                    })}
                  </p>
                  {blockers.length > 0 ? (
                    <ul className="mt-1 list-disc pl-5">
                      {blockers.map((blocker) => (
                        <li key={blocker.code}>
                          {t(`efs.operations.dispatch.delete_dialog.blockers.${blocker.code}`, {
                            count: blocker.count,
                            defaultValue: `${blocker.code} (${blocker.count})`,
                          })}
                        </li>
                      ))}
                    </ul>
                  ) : countOrZero(preview.blocker_count) > 0 ? (
                    <p className="mt-1 text-xs">
                      {t('efs.operations.dispatch.delete_dialog.blocked_fallback', {
                        count: countOrZero(preview.blocker_count),
                        defaultValue:
                          '{{count}} 件の関連データを特定できませんでした。再読み込みしても解消しない場合は管理者に確認してください。',
                      })}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : (
            <LocalizedApiError error={previewError} showTechnicalDetails={false} />
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="dispatch-delete-reason">
            {t('efs.operations.dispatch.delete_dialog.reason_label', {
              defaultValue: '削除理由（必須）',
            })}
          </Label>
          <Textarea
            ref={reasonRef}
            id="dispatch-delete-reason"
            rows={3}
            maxLength={2000}
            value={reason}
            disabled={isPending}
            onChange={(e) => {
              setReason(e.target.value);
              if (e.target.value.trim()) setReasonError(null);
            }}
            placeholder={t('efs.operations.dispatch.delete_dialog.reason_placeholder', {
              defaultValue: '例：重複作成のため、関連する入庫・解体データごと削除します',
            })}
            aria-invalid={!!reasonError}
            aria-describedby={reasonError ? 'dispatch-delete-reason-error' : undefined}
            autoFocus
          />
          {reasonError ? (
            <p id="dispatch-delete-reason-error" role="alert" className="text-sm text-red-900">
              {reasonError}
            </p>
          ) : (
            <p className="text-xs text-efs-text-muted">
              {t('efs.operations.dispatch.delete_dialog.reason_help', {
                defaultValue:
                  'この理由は復元・監査時に確認するため、キャンセル理由とは別に記録します。',
              })}
            </p>
          )}
        </div>

        <LocalizedApiError error={error} showTechnicalDetails={false} />

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            {t('efs.common.actions.cancel', { ns: 'common', defaultValue: 'キャンセル' })}
          </Button>
          <Button type="button" variant="destructive" disabled={!canSubmit} onClick={submit}>
            {isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Trash2 className="mr-1 h-4 w-4" aria-hidden />
            )}
            {t('efs.operations.dispatch.delete_dialog.confirm', {
              defaultValue: '削除する',
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
